/*
 * Lynkaz i18n runtime (i18next underneath).
 *
 * Messages are identified by their English text (gettext style): `t('Purchase status')`.
 * English therefore needs no catalog - the code is the source - and every missing translation
 * falls back to the English text. Catalogs live in src/i18n/locales/<lng>/{ui,server}.json and are
 * maintained with `npm run i18n:extract | i18n:check | i18n:translate` (see docs/i18n.md).
 *
 * The active language is resolved and its catalog loaded BEFORE the app modules are imported
 * (see src/index.js), so module-level constants such as column definitions are already translated.
 * Changing language persists the choice and reloads the page; that keeps grids, cached menus and
 * module-level constants consistent without every component having to subscribe to changes.
 *
 * Keep this module free of app imports (store, Helper, Settings): it must evaluate first.
 */
import i18next from 'i18next';
import LANGUAGES, { ENABLED_LANGUAGES, SOURCE_LANGUAGE, findLanguage } from './languages';

const STORAGE_KEY = 'lynkaz.language';
const NAMESPACES = ['ui', 'server'];
const isDev = process.env.NODE_ENV === 'development';

let currentLanguage = SOURCE_LANGUAGE;
let ready = false;
let normalizedIndex = null;
let languageChangeHandler = null;
const missingSeen = new Set();
// Every catalog translation t() has returned (without interpolation); bounded by the catalog size.
const producedTranslations = new Set();

function readStoredLanguage() {
    try {
        return window.localStorage.getItem(STORAGE_KEY);
    } catch (e) {
        return null;
    }
}

function storeLanguage(code) {
    try {
        window.localStorage.setItem(STORAGE_KEY, code);
    } catch (e) {
        // private mode / blocked storage: the choice simply lasts for this page view
    }
}

// ?lang=xx (handy for support links and testing) > saved choice > English.
// The browser language is deliberately not used: existing users keep English until they choose otherwise.
function resolveInitialLanguage() {
    let fromUrl = null;
    try {
        fromUrl = new URLSearchParams(window.location.search).get('lang');
    } catch (e) {
        fromUrl = null;
    }
    const lang = findLanguage(fromUrl) || findLanguage(readStoredLanguage());
    if (fromUrl && lang) storeLanguage(lang.code);
    return lang ? lang.code : SOURCE_LANGUAGE;
}

function loadCatalog(lng, ns) {
    return import(/* webpackChunkName: "i18n-[request]" */ `./locales/${lng}/${ns}.json`).then(m => m.default || m);
}

function applyDocumentLanguage(lng) {
    const info = LANGUAGES.find(l => l.code === lng) || LANGUAGES[0];
    const html = document.documentElement;
    html.setAttribute('lang', info.code);
    html.setAttribute('dir', info.dir);
    html.classList.toggle('rtl', info.dir === 'rtl');
}

export async function initI18n() {
    let lng = resolveInitialLanguage();
    const resources = {};
    if (lng !== SOURCE_LANGUAGE) {
        try {
            const catalogs = await Promise.all(NAMESPACES.map(ns => loadCatalog(lng, ns)));
            resources[lng] = {};
            NAMESPACES.forEach((ns, i) => { resources[lng][ns] = catalogs[i]; });
        } catch (e) {
            console.error(`[i18n] could not load the "${lng}" catalog, using English`, e);
            lng = SOURCE_LANGUAGE;
        }
    }
    await i18next.init({
        lng,
        resources,
        ns: NAMESPACES,
        defaultNS: 'ui',
        fallbackNS: 'server',
        fallbackLng: false, // the English msgid itself is the fallback
        keySeparator: false, // msgids are sentences: '.' and ':' are not structure
        nsSeparator: false,
        returnNull: false,
        returnEmptyString: false,
        interpolation: { escapeValue: false }, // React escapes; jQuery callers already handled raw text
        initImmediate: false,
    });
    currentLanguage = lng;
    normalizedIndex = null;
    ready = true;
    applyDocumentLanguage(lng);
    return lng;
}

/* ---------- lookup ---------- */

// Case, surrounding whitespace and a trailing full stop do not change meaning, so
// "No Record Found" / "No record found." share one translation.
export function normalizeMessage(text) {
    return String(text).replace(/\s+/g, ' ').trim().replace(/\.+$/, '').toLowerCase();
}

function getNormalizedIndex() {
    if (normalizedIndex) return normalizedIndex;
    normalizedIndex = new Map();
    NAMESPACES.forEach(ns => {
        const bundle = i18next.getResourceBundle(currentLanguage, ns) || {};
        Object.keys(bundle).forEach(key => {
            const value = bundle[key];
            if (typeof value === 'string' && value && !/_(zero|one|two|few|many|other)$/.test(key)) {
                const nk = normalizeMessage(key);
                if (!normalizedIndex.has(nk)) normalizedIndex.set(nk, value);
            }
        });
    });
    return normalizedIndex;
}

function getResource(key) {
    for (const ns of NAMESPACES) {
        const value = i18next.getResource(currentLanguage, ns, key);
        if (typeof value === 'string' && value) return value;
    }
    return null;
}

function isAllCaps(text) {
    return /\p{L}/u.test(text) && text === text.toUpperCase() && text !== text.toLowerCase();
}

function lookup(text) {
    const exact = getResource(text);
    if (exact) return exact;
    const trimmed = text.trim();
    if (trimmed !== text) {
        const t2 = getResource(trimmed);
        if (t2) return text.replace(trimmed, t2);
    }
    const normalized = getNormalizedIndex().get(normalizeMessage(text));
    if (normalized) return isAllCaps(trimmed) ? normalized.toLocaleUpperCase(currentLanguage) : normalized;
    return null;
}

function reportMissing(text) {
    if (!isDev || missingSeen.has(text)) return;
    missingSeen.add(text);
    window.__i18nMissing = missingSeen;
    console.debug(`[i18n] missing "${currentLanguage}" translation:`, text);
}

function interpolate(text, vars) {
    if (!vars) return text;
    if (ready) return i18next.services.interpolator.interpolate(text, vars, currentLanguage, {});
    return text.replace(/\{\{\s*([\w.]+)\s*\}\}/g, (m, name) => (vars[name] !== undefined && vars[name] !== null ? String(vars[name]) : m));
}

/**
 * Translate an English UI string. Non-strings (numbers, React nodes, null) are returned unchanged,
 * so it is safe on values that may or may not be text.
 * @param {string} text English source text, may contain {{placeholders}}
 * @param {object} [vars] placeholder values
 */
export function t(text, vars) {
    if (typeof text !== 'string' || text === '') return text;
    if (!ready || currentLanguage === SOURCE_LANGUAGE) return interpolate(text, vars);
    const translated = lookup(text);
    if (translated === null) {
        reportMissing(text);
        return interpolate(text, vars);
    }
    if (!vars && translated !== text) producedTranslations.add(translated);
    return interpolate(translated, vars);
}

/**
 * Plural-aware translation. Catalog keys follow the i18next v4 JSON convention: `<other>_one`, `<other>_other`, ...
 * @example tn(count, '{{count}} line selected', '{{count}} lines selected')
 */
export function tn(count, one, other, vars) {
    const values = { count, ...vars };
    if (ready && currentLanguage !== SOURCE_LANGUAGE) {
        const category = pluralCategory(count);
        const translated = getResource(`${other}_${category}`) || getResource(`${other}_other`);
        if (translated) return interpolate(translated, values);
        reportMissing(other);
    }
    return interpolate(Number(count) === 1 ? one : other, values);
}

function pluralCategory(count) {
    try {
        return new Intl.PluralRules(getLanguageInfo().intl).select(Number(count));
    } catch (e) {
        return Number(count) === 1 ? 'one' : 'other';
    }
}

/**
 * Marks English text for extraction without translating it (gettext's N_()). Use it for strings that
 * double as identifiers - e.g. view names that code compares - and translate them where they are
 * displayed with translateOnce()/t().
 */
export function msg(text) {
    return text;
}

/**
 * For shared components that translate at display time but may receive text the caller already
 * translated (e.g. a grid headerName built with getLabel). Text that t() already produced in this page
 * view is returned as is, so "Date" -> "Data" is not translated a second time as the English word "Data".
 * (Tracking actual output rather than every catalog value matters: Italian "Date" is also the translation
 * of "Dates", and the English word "Date" must still be translated.)
 */
export function translateOnce(text) {
    if (typeof text !== 'string' || !text || !ready || currentLanguage === SOURCE_LANGUAGE) return text;
    if (producedTranslations.has(text)) return text;
    return t(text);
}

/** True when the active language has a translation for this English text (exact or normalised). */
export function hasTranslation(text) {
    if (typeof text !== 'string' || !text) return false;
    if (currentLanguage === SOURCE_LANGUAGE) return true;
    return lookup(text) !== null;
}

/* ---------- language state ---------- */

export function getLanguage() {
    return currentLanguage;
}

export function getLanguageInfo() {
    return LANGUAGES.find(l => l.code === currentLanguage) || LANGUAGES[0];
}

export function isRtl() {
    return getLanguageInfo().dir === 'rtl';
}

export { ENABLED_LANGUAGES as languages, SOURCE_LANGUAGE };

/**
 * Lets the app run work (e.g. saving the preference to the user profile) before the reload.
 * The handler receives the language code and may return a promise.
 */
export function setLanguageChangeHandler(handler) {
    languageChangeHandler = handler;
}

/** Persist the language locally only (no reload). Used when the user profile dictates the language. */
export function rememberLanguage(code) {
    const lang = findLanguage(code);
    if (lang) storeLanguage(lang.code);
    return lang ? lang.code : null;
}

/**
 * Switch the UI language: save it, give the change handler up to 3 s, then reload the current page.
 * @returns {Promise<boolean>} false when the code is unsupported or already active
 */
export async function changeLanguage(code) {
    const lang = findLanguage(code);
    if (!lang || lang.code === currentLanguage) return false;
    storeLanguage(lang.code);
    if (languageChangeHandler) {
        try {
            await Promise.race([
                Promise.resolve(languageChangeHandler(lang.code)),
                new Promise(resolve => setTimeout(resolve, 3000)),
            ]);
        } catch (e) {
            console.warn('[i18n] language change handler failed', e);
        }
    }
    reloadWithoutLangParam();
    return true;
}

function reloadWithoutLangParam() {
    const url = new URL(window.location.href);
    if (url.searchParams.has('lang')) {
        url.searchParams.delete('lang');
        window.location.replace(url.toString());
    } else {
        window.location.reload();
    }
}

export default i18next;
